import { useEffect, useMemo, useState, type ReactNode, type Ref } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ChevronDown, ChevronLeft, ChevronRight, Hospital } from 'lucide-react';
import { Banner } from '@/components/ui/Banner';
import { Button } from '@/components/ui/Button';
import { FieldError, FieldLabel } from '@/components/ui/Field';
import { Radio } from '@/components/ui/Radio';
import { Textarea } from '@/components/ui/Textarea';
import { isLatin1 } from '@/lib/referral/xref01Validation';
import { FORM_ERROR_TYPE, FORM_HINT_TYPE, FORM_LABEL_TYPE, TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

export function Section({
  title,
  hint,
  id,
  children,
}: {
  title?: string;
  hint?: ReactNode;
  id?: string;
  children: ReactNode;
}) {
  return (
    <section id={id} className="hv-sec">
      {title ? <h3>{title}</h3> : null}
      {hint ? <p className="hint">{hint}</p> : null}
      <div className="body">{children}</div>
    </section>
  );
}

export function Grid({ children }: { children: ReactNode }) {
  return <div className="grid gap-s4 nav:grid-cols-2">{children}</div>;
}

export function ReadOnly({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className={FORM_LABEL_TYPE}>{label}</div>
      <p className={cn('mt-s1 break-words', TYPE.body, 'text-ds-text')}>{value}</p>
    </div>
  );
}

export function RadioGroup<T extends string>({
  name,
  legend,
  value,
  options,
  onChange,
}: {
  name: string;
  legend: string;
  /** Empty string means nothing selected (used by XREF02 pregnancy). */
  value: T | '';
  options: readonly { value: T; label: string }[];
  onChange: (value: T) => void;
}) {
  return (
    <fieldset>
      <legend className={FORM_LABEL_TYPE}>{legend}</legend>
      <div className="mt-s2 flex flex-col">
        {options.map((option) => (
          <Radio
            key={option.value}
            name={name}
            value={option.value}
            checked={value === option.value}
            onChange={() => onChange(option.value)}
            label={option.label}
          />
        ))}
      </div>
    </fieldset>
  );
}

export function TextArea({
  id,
  fieldLabel,
  value,
  onChange,
  max,
  textRef,
}: {
  id: string;
  fieldLabel: string;
  value: string;
  onChange: (value: string) => void;
  max: number;
  textRef?: Ref<HTMLTextAreaElement>;
}) {
  const { t } = useTranslation();
  const over = value.length > max;
  const latin1 = isLatin1(value);
  return (
    <div>
      <Textarea
        id={id}
        ref={textRef}
        rows={4}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-invalid={over || !latin1 || undefined}
        className={over || !latin1 ? 'border-[1.5px] border-ds-red' : undefined}
      />
      <p className={cn('mt-s1 text-right', over ? FORM_ERROR_TYPE : FORM_HINT_TYPE)}>
        {t('patients.hospital_referral.counter', '{{count}} / {{max}}', { count: value.length, max })}
      </p>
      {!latin1 && (
        <p role="alert" className={cn('mt-s1', FORM_ERROR_TYPE)}>
          {t(
            'patients.hospital_referral.latin1',
            '{{field}} indeholder et tegn, der ikke kan skrives i ISO-8859-1.',
            { field: fieldLabel },
          )}
        </p>
      )}
    </div>
  );
}

export function LabeledText({
  id,
  label,
  value,
  onChange,
  textRef,
  max,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  textRef?: Ref<HTMLTextAreaElement>;
  /** Per-field MedCom limit. When set, a counter is shown and the field turns red above it. */
  max?: number;
}) {
  const { t } = useTranslation();
  const over = max !== undefined && value.length > max;
  const latin1 = isLatin1(value);
  return (
    <div>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <div className="mt-s1">
        <Textarea
          id={id}
          ref={textRef}
          rows={4}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          aria-invalid={over || !latin1 || undefined}
          className={over || !latin1 ? 'border-[1.5px] border-ds-red' : undefined}
        />
      </div>
      {max !== undefined && (
        <p className={cn('mt-s1 text-right', over ? FORM_ERROR_TYPE : FORM_HINT_TYPE)}>
          {t('patients.hospital_referral.counter', '{{count}} / {{max}}', { count: value.length, max })}
        </p>
      )}
      {over && (
        <FieldError>
          {t(
            'patients.hospital_referral.too_long',
            '{{field}} er {{length}} tegn. Grænsen er {{max}}. Værdien afkortes ikke.',
            { field: label, length: value.length, max },
          )}
        </FieldError>
      )}
      {!latin1 && (
        <p role="alert" className={cn('mt-s1', FORM_ERROR_TYPE)}>
          {label} indeholder et tegn, der ikke kan skrives i ISO-8859-1.
        </p>
      )}
    </div>
  );
}

/**
 * Why "Dan henvisning" did not produce a referral. Stays next to the button until the
 * next attempt, so a refusal is not lost when the toast fades.
 */
export function ReferralSubmitError({ message }: { message: string | null }) {
  const { t } = useTranslation();
  if (!message) return null;
  return (
    <Banner
      tone="error"
      title={t('patients.hospital_referral.submit_failed_title', 'Henvisningen blev ikke dannet')}
    >
      <span className="whitespace-pre-wrap">{message}</span>
    </Banner>
  );
}

export function ReferralBlockingNotice({
  messages,
  relationsTo,
  relativesHref,
}: {
  messages: readonly string[];
  relationsTo?: string;
  relativesHref?: string;
}) {
  const { t } = useTranslation();
  if (messages.length === 0) return null;
  return (
    <div className="hv-block" role="alert">
      <AlertTriangle className="h-5 w-5" aria-hidden />
      <div>
        <b>{t('patients.hospital_referral.cannot_build_yet', 'Henvisningen kan ikke dannes endnu')}</b>
        {messages.map((message) => (
          <p key={message}>{message}</p>
        ))}
        {relationsTo || relativesHref ? (
          <div className="a">
            {relationsTo ? (
              <Link to={relationsTo}>
                {t('patients.hospital_referral.go_to_relations', 'Gå til Relationer')}
                <ChevronRight className="h-3.5 w-3.5" aria-hidden />
              </Link>
            ) : null}
            {relativesHref ? (
              <a href={relativesHref}>
                {t('patients.hospital_referral.go_to_relatives', 'Gå til Pårørende i formularen')}
                <ChevronDown className="h-3.5 w-3.5" aria-hidden />
              </a>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export type ReferralTocItem = { id: string; label: string };

function useActiveSection(ids: readonly string[]) {
  const [active, setActive] = useState(ids[0] ?? '');
  useEffect(() => {
    if (ids.length === 0) return;
    const elements = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);
    if (elements.length === 0) return;
    const visible = new Map<string, number>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visible.set(entry.target.id, entry.intersectionRatio);
          else visible.delete(entry.target.id);
        }
        const next = ids.find((id) => visible.has(id));
        if (next) setActive(next);
      },
      { rootMargin: '-80px 0px -55% 0px', threshold: [0, 0.25, 0.5] },
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [ids]);
  return active;
}

export function ReferralFormChrome({
  title,
  subtitle,
  backTo,
  backLabel,
  children,
  footer,
  onCancel,
  cancelLabel,
  footerStatus,
  banner,
  toc,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  backTo?: string;
  backLabel?: string;
  children: ReactNode;
  footer: ReactNode;
  onCancel?: () => void;
  cancelLabel?: string;
  footerStatus?: ReactNode;
  banner?: ReactNode;
  toc?: readonly ReferralTocItem[];
}) {
  const { t } = useTranslation();
  const tocKey = toc?.map((item) => item.id).join('|') ?? '';
  const tocIds = useMemo(() => (tocKey ? tocKey.split('|') : []), [tocKey]);
  const active = useActiveSection(tocIds);
  return (
    <div className="hv-form">
      {backTo ? (
        <nav className="hv-crumb" aria-label={t('common.breadcrumb', 'Brødkrumme')}>
          <Link to={backTo}>
            <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
            {backLabel}
          </Link>
          <span className="sep" aria-hidden>
            /
          </span>
          <span>{title}</span>
        </nav>
      ) : null}
      <div className="hv-dh">
        <div>
          <div className="hl">
            <h2>{title}</h2>
          </div>
          {subtitle ? <p className="hv-sub">{subtitle}</p> : null}
        </div>
      </div>
      {banner}
      {toc && toc.length > 0 ? (
        <div className="fp-layout">
          <nav className="fp-toc" aria-label={t('common.contents', 'Indholdsfortegnelse')}>
            {toc.map((item) => (
              <a key={item.id} href={`#${item.id}`} aria-current={active === item.id ? 'true' : undefined}>
                {item.label}
              </a>
            ))}
          </nav>
          <div className="fp-main">{children}</div>
        </div>
      ) : (
        children
      )}
      <div className="hv-foot">
        <span className="st">
          {footerStatus ?? (
            <>
              <Hospital className="h-4 w-4" aria-hidden />
              <span className="min-w-0 truncate">
                {t('patients.hospital_referral.footer_receiver_empty', 'Ingen modtager valgt')}
              </span>
            </>
          )}
        </span>
        <div className="r">
          {onCancel ? (
            <Button type="button" variant="secondary" onClick={onCancel}>
              {cancelLabel ?? t('common.cancel')}
            </Button>
          ) : null}
          {footer}
        </div>
      </div>
    </div>
  );
}
